// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import React from 'react'
import { array, func, number, object, oneOfType, string } from 'prop-types'

class Row extends React.Component {
  static propTypes = {
    columns: array.isRequired,
    customProps: object,
    idRoot: string.isRequired,
    rowClass: oneOfType([
      func,
      string,
    ]),
    rowClick: func,
    rowData: object.isRequired,
    rowIndex: number.isRequired,
    TooltipTrigger: func.isRequired,
    unitType: string,
  }

  state = {
    mouseDownX: null,
    mouseDownY: null,
  }

  // A render control mechanism can be placed on each row if it is so desired.
  // This can improve performance when rendering large results sets with multiple columns
  // If it isn't, the row will just rerender based on react's natural lifecycle.
  shouldComponentUpdate (np) {
    const {
      customProps = {},
    } = this.props

    const { shouldRowUpdate } = customProps

    if (shouldRowUpdate) {
      return shouldRowUpdate(this.props, np)
    } else {
      return true
    }
  }

  // captures when the row's clicked and applies the handler if there is one.
  // NOTE - we do not track mousedown/mouseup here. Instead we track mousedown/click
  // because the row can have children which have click handlers assigned to them
  // but mouseup will fire on the row before the child click handler can prevent propagation
  // to this row.
  handleClick = e => {
    const {
      clientX,
      clientY,
    } = e

    const {
      mouseDownX,
      mouseDownY,
    } = this.state

    this.setState(() => ({
      mouseDownX: null,
      mouseDownY: null,
    }))

    const {
      rowClick,
      rowData,
    } = this.props

    // RE: LEGO-5254
    // if there's a click handler for the row and if the mouse has moved more than 10 px on X or Y, then
    // do not fire the rowClick handler. this should be interpreted as the user trying to highlight something.
    // NOTE - This approach was added after a few years of development when users asked that on
    // top of clicking the row to select it, they would also like to be able to highlight text
    // within the table's cells. Comparing the X and Y coordinates does not provide clear
    // accessability support. As a result, this approach should be scrutinized and it we find
    // it does not adequately support user's needs, we will need to take another approach.
    // It was decided that this approach should be sufficient for the time being and is the
    // easiest way to implement highlight support on the existing clickable rows.
    // TODO - this is implemented in a few different places currently.
    // This should be refactored to be a single hook or HOC (with a hook).
    if (
      rowClick &&
      Math.abs(mouseDownX - clientX) < 10 &&
      Math.abs(mouseDownY - clientY) < 10
    ) {
      return rowClick(rowData, e)
    }
  }

  // capture coordinates for where the user fired mousedown
  handleMouseDown = ({ clientX, clientY }) => (
    this.setState(() => ({
      mouseDownX: clientX,
      mouseDownY: clientY,
    }))
  )

  getRowClasses = () => {
    const {
      customProps = {},
      rowClass,
      rowClick,
      rowData,
    } = this.props

    const {
      highlightedRows,
    } = customProps

    // styles for the row
    // can customize with either a string values
    // or a function which returns a string based on row data
    let trc = rowClick ? 'pointer' : ''

    // if this table knows about highlighted, rows, check to see if this row is highlighted.
    if (highlightedRows) {
      const uniqueValue = rowData[rowData.uniqueId]
      trc += (
        ~highlightedRows.indexOf(uniqueValue)
          ? ' highlighted-row'
          : ''
      )
    }

    // if there's any special row classes to apply, add em here
    if (typeof rowClass === 'string') {
      trc += ` ${rowClass}`
    } else if (typeof rowClass === 'function') {
      trc += ` ${rowClass(rowData)}`
    }

    return trc
  }

  render () {
    const {
      columns,
      customProps,
      idRoot,
      rowData,
      rowIndex,
      TooltipTrigger,
      unitType,
    } = this.props

    const { uniqueId } = rowData

    const rowId = `${idRoot}-${uniqueId}-${rowData[uniqueId]}`

    // uniqueId must be defined to properly assign ids per QA request.
    // do not gracefully handle missing uniqueId in DataTable.
    if (!uniqueId) {
      console.warn(`Warning: uniqueId was not defined in DataTable ${idRoot}`)
      return null
    }

    return (
      <tr
        className={this.getRowClasses()}
        id={rowId}
        onClick={this.handleClick}
        onMouseDown={this.handleMouseDown}>
        {columns.map((c, i) => (
          <td
            key={`${rowId}-${c.columnKey}`}
            className={`td-${c.columnKey}`}
            id={`${rowId}-${c.columnKey}`}>
            {c.cellFormatter
              ? (
                <c.cellFormatter.default
                  columnKey={c.columnKey}
                  customProps={customProps}
                  idRoot={`${rowId}-${c.columnKey}`}
                  rowData={rowData}
                  rowIndex={rowIndex}
                  TooltipTrigger={TooltipTrigger}
                  unitType={unitType} />
              ) : (
                <span title={rowData[c.columnKey]}>
                  {rowData[c.columnKey]}
                </span>
              )
            }
          </td>
        ))}
      </tr>
    )
  }
}

export default Row
